import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileOutput, FileType2, Loader2, Upload, Sparkles, Copy, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { aiApi } from "./aiApi";
import SignToggle from "./SignToggle";

const WORD_ACCEPT = ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Skill Word → PDF: pilih fail Word, kemudian Mimi tanya - Save As PDF terus atau Jana PDF baharu. */
export default function AIWordToPdfDialog({ open, cid, canUpload, onClose, onCreated, onAsk }) {
  const [files, setFiles] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState("");
  const [sign, setSign] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPick(null); setFiles(null); setSign(false);
    aiApi.myFiles("", "word").then((d) => setFiles((d.files || []).filter((f) => f.mime?.includes("wordprocessingml")))).catch(() => setFiles([]));
  }, [open]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("upload");
    try { setPick(await aiApi.upload(f)); } catch (err) { toast.error(err.response?.data?.detail || "Gagal muat naik"); } finally { setBusy(""); }
  };
  const saveAs = async () => {
    setBusy("saveas");
    try { onCreated(await aiApi.wordToPdf({ file_id: pick.id, conversation_id: cid, sign })); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan sebagai PDF"); } finally { setBusy(""); }
  };
  const generate = () => {
    onAsk(`Jana PDF baharu daripada dokumen Word '${pick.filename}' (file_id=${pick.id}). Baca dengan read_word dan susun semula sebagai PDF kemas.`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="z-[10002] max-w-md rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="ai-word-pdf-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileOutput className="h-5 w-5 text-pink-500" /> Word → PDF</DialogTitle></DialogHeader>
        {!pick ? (
          <div className="space-y-2">
            <div className="text-xs font-bold text-slate-600">Pilih fail Word (.doc / .docx)</div>
            {canUpload && (
              <label className="h-10 rounded-2xl border border-dashed border-slate-300 text-xs font-semibold text-slate-600 flex items-center justify-center gap-1.5 cursor-pointer hover:bg-slate-50" data-testid="ai-word-pdf-upload-btn">
                {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Muat naik fail Word
                <input type="file" accept={WORD_ACCEPT} hidden onChange={onUpload} data-testid="ai-word-pdf-upload-input" />
              </label>
            )}
            <div className="max-h-60 overflow-y-auto space-y-1" data-testid="ai-word-pdf-file-list">
              {files === null && <div className="h-16 grid place-items-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>}
              {files?.length === 0 && <div className="text-xs text-slate-400 p-2" data-testid="ai-word-pdf-empty">Tiada fail Word lagi{canUpload ? " - muat naik di atas" : ""}.</div>}
              {files?.map((f) => (
                <button key={f.id} onClick={() => setPick(f)} data-testid="ai-word-pdf-file"
                  className="w-full flex items-center gap-2 rounded-xl px-2.5 py-2 text-left hover:bg-pink-50 transition-colors">
                  <FileType2 className="h-4 w-4 shrink-0 text-sky-600" />
                  <span className="min-w-0 flex-1"><span className="block text-xs truncate text-slate-700">{f.filename}</span>
                    <span className="block text-[10px] text-slate-400">{(f.created_at || "").slice(0, 10)}</span></span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-2xl bg-slate-50 border border-slate-100 p-2.5">
              <FileType2 className="h-5 w-5 text-sky-600 shrink-0" />
              <span className="text-xs font-semibold text-slate-700 truncate flex-1" data-testid="ai-word-pdf-picked">{pick.filename}</span>
              <button onClick={() => setPick(null)} className="text-slate-400 hover:text-slate-700" aria-label="Tukar fail" data-testid="ai-word-pdf-back"><ArrowLeft className="h-4 w-4" /></button>
            </div>
            <div className="text-[13px] font-bold text-slate-800" data-testid="ai-word-pdf-question">Mimi: Nak jana PDF baharu atau terus Save As?</div>
            <button onClick={saveAs} disabled={!!busy} data-testid="ai-word-pdf-saveas-btn"
              className="w-full flex items-start gap-3 rounded-2xl border-2 border-pink-200 bg-pink-50/60 p-3 text-left hover:border-pink-400 transition-colors disabled:opacity-60">
              <span className="h-9 w-9 shrink-0 rounded-xl bg-pink-500 text-white grid place-items-center">{busy === "saveas" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}</span>
              <span><span className="block text-[13px] font-bold text-slate-800">1. Save As PDF terus</span>
                <span className="block text-[11px] text-slate-500">Salinan tepat fail Word asal - teks, fon, susun atur & logo tidak diubah. Segera, tanpa AI.</span></span>
            </button>
            <SignToggle checked={sign} onChange={setSign} testid="ai-word-pdf-sign-toggle" />
            <button onClick={generate} disabled={!!busy} data-testid="ai-word-pdf-generate-btn"
              className="w-full flex items-start gap-3 rounded-2xl border-2 border-slate-200 p-3 text-left hover:border-amber-400 transition-colors disabled:opacity-60">
              <span className="h-9 w-9 shrink-0 rounded-xl bg-amber-400 text-white grid place-items-center"><Sparkles className="h-4 w-4" /></span>
              <span><span className="block text-[13px] font-bold text-slate-800">2. Jana PDF baharu</span>
                <span className="block text-[11px] text-slate-500">Mimi baca & susun semula kandungan (cth kepala surat rasmi Destiny Junior).</span></span>
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
